import { Component, Output, EventEmitter, Input, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ButtonComponent } from '../../button/button.component';
import { SelectComponent } from '../../select/select.component';
import { InputCheckboxComponent } from '../../input-checkbox/input-checkbox.component';
import { AlertCardComponent } from '../../alert-card/alert-card.component';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AttachmentService } from '../../../../../../../libs/shared/src/lib/services/attachment.service';
import { environment } from '../../../../environments/environment';
import { catchError, distinctUntilChanged, map, of, Subject, takeUntil } from 'rxjs';

export enum ProfileFillMethod {
  AI_AUTOFILL = 'ai_autofill',
  MANUAL_ENTRY = 'manual_entry',
}

export interface RateLimitInfo {
  message?: string;
  retryAfterHours?: number;
  retryAfterMinutes?: number;
  retryAfter?: number;
}

@Component({
  selector: 'app-profile-fill-options',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslocoPipe, ButtonComponent, SelectComponent, InputCheckboxComponent, AlertCardComponent],
  templateUrl: './profile-fill-options.component.html',
  styleUrls: ['./profile-fill-options.component.scss'],
})
export class ProfileFillOptionsComponent implements OnInit, OnDestroy, OnChanges {
  @Input() isAuthenticated = false;
  @Input() showCvParsingLoader = false;
  @Input() isParsingComplete = false;
  @Input() rateLimitInfo: RateLimitInfo | null = null;
  @Input() hasExistingProfileData = false; // Whether user already has CV data (skills, education, etc.)
  @Input() aiParsingConsentPreference = false;
  @Input() aiParsingConsentUpdating = false;
  @Output() methodSelected = new EventEmitter<ProfileFillMethod>();
  @Output() fileProcessed = new EventEmitter<{ file: File | null; language: string; attachmentId?: string; resumeUrl?: string; aiParsingConsent: boolean }>();
  @Output() aiParsingConsentPreferenceChange = new EventEmitter<boolean>();

  resumeAndCoverForm!: FormGroup;
  toFile: FileList | null = null;
  isUploading = false;
  aiParsingConsentHtml = '';
  overwriteConfirmationHtml = '';
  isAiSectionExpanded = true; // Controls whether AI parsing section is expanded

  ProfileFillMethod = ProfileFillMethod; // Make enum accessible in template

  // Cleanup
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private attachmentService: AttachmentService,
    private translocoService: TranslocoService
  ) {
    this.initForms();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Collapse AI section when parsing completes
    if (changes['isParsingComplete'] && changes['isParsingComplete'].currentValue === true) {
      this.isAiSectionExpanded = false;
    }

    if (changes['aiParsingConsentPreference'] && this.resumeAndCoverForm) {
      this.syncAiParsingConsentControl();
    }
  }

  ngOnInit(): void {
    // Collapse AI section by default when user has existing profile data
    if (this.hasExistingProfileData) {
      this.isAiSectionExpanded = false;
    }

    this.syncAiParsingConsentControl();

    // Subscribe to translation changes - this handles both initial load and language changes
    this.translocoService.selectTranslate('jobApply.aiParsingConsent.description')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.generateAiParsingConsentHtml();
        this.generateOverwriteConfirmationHtml();
      });

    // Reset parsing completion when file or language changes
    this.resumeAndCoverForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (this.isParsingComplete && (value.resumeFile || value.resumeLanguage)) {
          // File or language changed after parsing - allow parsing again
          this.isParsingComplete = false;
        }
      });

    this.resumeAndCoverForm.get('aiParsingConsent')?.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((granted: boolean) => {
        this.aiParsingConsentPreferenceChange.emit(!!granted);
      });
  }

  /**
   * Toggle the AI parsing section expanded/collapsed state
   */
  toggleAiSection(): void {
    this.isAiSectionExpanded = !this.isAiSectionExpanded;
  }

  initForms(): void {
    this.resumeAndCoverForm = this.fb.group({
      resumeFile: [null, Validators.required],
      resumeLanguage: ['', Validators.required],
      attachmentId: [''],
      resumeUrl: [''],
      aiParsingConsent: [false, Validators.requiredTrue],
      overwriteConfirmation: [false], // Dynamically required when hasExistingProfileData is true
    });
  }

  private syncAiParsingConsentControl(): void {
    this.resumeAndCoverForm.get('aiParsingConsent')?.setValue(!!this.aiParsingConsentPreference, { emitEvent: false });
  }

  /**
   * Check if form is valid considering the overwrite confirmation requirement
   */
  isFormValidForAiParsing(): boolean {
    const baseFormValid = this.resumeAndCoverForm.get('resumeFile')?.valid &&
                          this.resumeAndCoverForm.get('resumeLanguage')?.valid &&
                          this.resumeAndCoverForm.get('aiParsingConsent')?.valid;

    if (!baseFormValid) return false;

    // If user has existing data, they must confirm overwrite
    if (this.hasExistingProfileData) {
      return this.resumeAndCoverForm.get('overwriteConfirmation')?.value === true;
    }

    return true;
  }

  selectMethod(method: ProfileFillMethod): void {
    if (method === ProfileFillMethod.AI_AUTOFILL) {
      // Don't proceed if parsing is already complete or in progress
      if (this.isParsingComplete || this.showCvParsingLoader) {
        return;
      }

      // Mark all fields as touched to show validation errors
      Object.keys(this.resumeAndCoverForm.controls).forEach(key => {
        this.resumeAndCoverForm.get(key)?.markAsTouched();
      });

      // For AI autofill, we need file, language, AI parsing consent, and overwrite confirmation if applicable
      if (this.isFormValidForAiParsing()) {
        // Now emit file processed when the user actively chooses AI autofill
        this.emitFileProcessed();
        this.methodSelected.emit(method);
      }
      // If form is invalid, validation errors will be shown
    } else {
      // For manual entry, no validation needed
      this.methodSelected.emit(method);
    }
  }

  onFileChange(event: Event, type: 'resume' | 'coverLetter'): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length) {
      this.toFile = input.files;
      const file = input.files[0];
      if (type === 'resume') {
        this.resumeAndCoverForm.patchValue({ resumeFile: file });
        this.processFile(file);
      }
    }
  }

  processFile(file: File): void {
    // Convert file to blob for processing
    this.isUploading = true;
    file.arrayBuffer().then((arrayBuffer: ArrayBuffer) => {
      const blob = new Blob([new Uint8Array(arrayBuffer)], {
        type: file.type,
      });

      // Send the blob to S3 via the attachment service
      this.sendUploadedFileToS3(blob, file);
    });
  }

  sendUploadedFileToS3(blob: Blob, file: File): void {
    const uploadObservable = this.isAuthenticated
      ? this.attachmentService.sendCandidateAttachment(blob, file.type, file.name)
      : this.attachmentService.sendNotLogInCandidateAttachment(blob, file.type);

    uploadObservable
      .pipe(
        map((res) => {
          // Handle different response formats for authenticated vs non-authenticated users
          if (res && res.body) {
            if (this.isAuthenticated) {
              // Authenticated users get object with ID
              const attachment = Array.isArray(res.body) ? res.body[0] : res.body;
              const attachmentId = attachment.id;
              this.resumeAndCoverForm.patchValue({ attachmentId: attachmentId });
            } else {
              // Non-authenticated users get S3 URL directly in body
              const attachment = Array.isArray(res.body) ? res.body[0] : res.body;
              const resumeUrl = typeof attachment === 'string' ? attachment : attachment.url;
              this.resumeAndCoverForm.patchValue({ resumeUrl: resumeUrl });
            }

            // Don't emit file processed here - wait for user to click "Use AI Autofill"
            this.isUploading = false;
          }
          return res;
        }),
        catchError((error) => {
          console.error('Error uploading file to S3:', error);
          this.isUploading = false;
          return of(null);
        })
      )
      .subscribe();
  }

  removeFile(type: 'resume' | 'coverLetter'): void {
    if (type === 'resume') {
      this.resumeAndCoverForm.patchValue({ resumeFile: null });
      this.toFile = null;
      // Emit null file to notify parent component
      this.emitFileProcessed();
    }
  }

  emitFileProcessed(): void {
    const file = this.resumeAndCoverForm.get('resumeFile')?.value;
    const language = this.resumeAndCoverForm.get('resumeLanguage')?.value || '';
    const attachmentId = this.resumeAndCoverForm.get('attachmentId')?.value;
    const resumeUrl = this.resumeAndCoverForm.get('resumeUrl')?.value;
    const aiParsingConsent = this.resumeAndCoverForm.get('aiParsingConsent')?.value || false;

    this.fileProcessed.emit({ file, language, attachmentId, resumeUrl, aiParsingConsent });
  }

  generateAiParsingConsentHtml(): void {
    const privacyUrl = environment.legal.privacyPolicyUrl;

    // Get the localized text template
    const template = this.translocoService.translate('jobApply.aiParsingConsent.description');

    // Replace placeholder with actual privacy policy link - reuse existing translation
    this.aiParsingConsentHtml = template
      .replace('{privacyLink}', `<a href="${privacyUrl}" target="_blank" class="consent-link">${this.translocoService.translate('jobApply.gdprConsent.privacyLinkText')}</a>`);
  }

  generateOverwriteConfirmationHtml(): void {
    // Get the localized description text for overwrite warning
    this.overwriteConfirmationHtml = this.translocoService.translate('profile.aiParsing.overwriteConfirmation.description');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
